import { useEffect, useMemo, useRef } from 'react'
import ReactQuill, { Quill } from 'react-quill'
import type { DeltaStatic } from 'quill'
import 'react-quill/dist/quill.snow.css'

// Quill 1(react-quill@2.0.0) — 1.0 화면(react-quill 1.3.5)과 같은 HTML(ql-align-*·ql-indent-*·<ol>/<ul>)을 쓰려고 Quill 2 가 아니다(LOGISPRD-15070).
const Delta = Quill.import('delta') as new () => DeltaStatic

/** 1.0 툴바 기준. 제목(H1·H2)·인용은 2026년 사용 0건이라 뺐고, 링크를 더했다. */
const TOOLBAR = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
  [{ align: [] }, { color: [] }, { background: [] }],
  ['link', 'clean'],
]
/** 서버 sanitize 허용 목록(p br strong em u s ol ul li a span)으로 나오는 서식만 — image 는 없다(신규 글은 본문 안 이미지 없음). */
const FORMATS = ['bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'indent', 'link', 'align', 'color', 'background']

const URL_PATTERN = /https?:\/\/[^\s<>"']+/g

/** 붙여넣은 글의 URL 을 링크로 바꾼다. */
const linkifyText = (node: Node, delta: DeltaStatic): DeltaStatic => {
  const text = node.textContent ?? ''
  const matches = Array.from(text.matchAll(URL_PATTERN))
  if (!matches.length) return delta
  const next = new Delta()
  const last = matches.reduce((from, m) => {
    const at = m.index ?? 0
    if (at > from) next.insert(text.slice(from, at))
    next.insert(m[0], { link: m[0] })
    return at + m[0].length
  }, 0)
  if (last < text.length) next.insert(text.slice(last))
  return next
}

const imageFiles = (list: FileList | null | undefined): File[] => Array.from(list ?? []).filter((f) => f.type.startsWith('image/'))

interface Props {
  value: string
  onChange: (html: string) => void
  /** 붙여넣기·끌어다 놓은 이미지 — 본문 대신 첨부로 올린다. */
  onImageFiles: (files: File[]) => void
}

export function BoardEditor({ value, onChange, onImageFiles }: Readonly<Props>) {
  const ref = useRef<ReactQuill>(null)
  const onImageFilesRef = useRef(onImageFiles)
  onImageFilesRef.current = onImageFiles

  const modules = useMemo(
    () => ({
      toolbar: TOOLBAR,
      clipboard: {
        matchVisual: false,
        matchers: [
          // 붙여넣은 HTML 의 이미지는 버린다 — 파일은 paste 이벤트에서 첨부로 올린다.
          ['img', () => new Delta()],
          [Node.TEXT_NODE, linkifyText],
        ],
      },
    }),
    [],
  )

  // 클립보드·드롭 이미지 파일은 Quill 이 base64 <img> 로 넣기 전에 가로챈다.
  useEffect(() => {
    const root = ref.current?.getEditor().root
    if (!root) return undefined
    const intercept = (files: File[], e: Event) => {
      if (!files.length) return
      e.preventDefault()
      e.stopPropagation()
      onImageFilesRef.current(files)
    }
    const onPaste = (e: ClipboardEvent) => intercept(imageFiles(e.clipboardData?.files), e)
    const onDrop = (e: DragEvent) => intercept(imageFiles(e.dataTransfer?.files), e)
    root.addEventListener('paste', onPaste, true)
    root.addEventListener('drop', onDrop, true)
    return () => {
      root.removeEventListener('paste', onPaste, true)
      root.removeEventListener('drop', onDrop, true)
    }
  }, [])

  return <ReactQuill ref={ref} theme="snow" value={value} onChange={onChange} modules={modules} formats={FORMATS} style={{ height: 320, marginBottom: 42 }} />
}
